import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '@/services/api';
import { hasCapability } from '@/lib/capabilities';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ChevronLeft, Printer, RefreshCw, CheckCircle2, XCircle, Clock } from 'lucide-react';

const STATUS_STYLES = {
  PENDING: { icon: Clock, cls: 'text-amber-600 bg-amber-50' },
  PRINTED: { icon: CheckCircle2, cls: 'text-green-600 bg-green-50' },
  FAILED: { icon: XCircle, cls: 'text-red-600 bg-red-50' },
};

export default function PrintQueue() {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const storeId = user?.store_id;
  const canOrder = hasCapability(user, 'orders.operate') || hasCapability(user, 'menu.manage');

  const [data, setData] = useState({ jobs: [], counts: {} });
  const [filter, setFilter] = useState('');
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    const res = await apiClient.get(`/printing/jobs/${storeId}`, { params: filter ? { status: filter } : {} });
    setData(res.data);
  }, [storeId, filter]);

  useEffect(() => {
    if (!user?.id) { navigate('/login'); return; }
    if (!canOrder) { toast.error('No permission'); navigate('/pos-dashboard'); return; }
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [load]);

  const reprint = async (job) => {
    try { await apiClient.post(`/printing/${job.id}/reprint`); toast.success('Re-queued for printing'); load(); }
    catch { toast.error('Reprint failed'); }
  };

  if (!storeId) return <div className="p-8 text-center">No store assigned.</div>;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="print-queue-page">
      <div className="bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between sticky top-0 z-30">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/pos-dashboard')} data-testid="pq-back"><ChevronLeft className="w-4 h-4" /></Button>
          <div className="flex items-center gap-2">
            <Printer className="w-5 h-5 text-sspice" />
            <div>
              <h1 className="font-heading text-lg font-bold text-slate-900">Print Queue</h1>
              <p className="text-xs text-slate-500">KOTs & receipts · detect failures & reprint</p>
            </div>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="pq-refresh"><RefreshCw className="w-4 h-4" /></Button>
      </div>

      <div className="max-w-3xl mx-auto p-4">
        <div className="flex gap-2 mb-4">
          {['', 'PENDING', 'PRINTED', 'FAILED'].map((s) => (
            <button key={s || 'ALL'} onClick={() => setFilter(s)} data-testid={`pq-filter-${s || 'ALL'}`}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold ${filter === s ? 'bg-sspice text-white' : 'bg-white border border-slate-200 text-slate-600'}`}>
              {s || 'All'}{s && data.counts[s] != null ? ` (${data.counts[s]})` : ''}
            </button>
          ))}
        </div>

        {data.jobs.length === 0 ? (
          <div className="text-center py-16 text-slate-400" data-testid="pq-empty">No print jobs.</div>
        ) : (
          <div className="space-y-2">
            {data.jobs.map((j) => {
              const st = STATUS_STYLES[j.status] || STATUS_STYLES.PENDING;
              const Icon = st.icon;
              return (
                <div key={j.id} className="flex items-center gap-3 p-3 bg-white border border-slate-200 rounded-xl" data-testid={`pq-job-${j.id}`}>
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${st.cls}`}><Icon className="w-5 h-5" /></div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-800">{j.type} · {j.copy_label} · Order #{j.order_number}</p>
                    <p className="text-xs text-slate-400">{j.status}{j.error ? ` — ${j.error}` : ''} · attempts {j.attempts}</p>
                  </div>
                  <button onClick={() => setPreview(j)} className="text-xs text-slate-500 hover:text-sspice" data-testid={`pq-view-${j.id}`}>View</button>
                  <Button size="sm" variant="outline" onClick={() => reprint(j)} data-testid={`pq-reprint-${j.id}`}>Reprint</Button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {preview && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setPreview(null)}>
          <div className="bg-white rounded-xl p-4 max-w-xs w-full" onClick={(e) => e.stopPropagation()}>
            <p className="font-semibold text-sm mb-2">{preview.type} preview</p>
            <pre className="text-[10px] leading-tight bg-slate-50 p-3 rounded overflow-auto max-h-96 whitespace-pre-wrap font-mono" data-testid="pq-preview-text">{preview.text_preview}</pre>
            <Button className="w-full mt-3 bg-sspice hover:bg-sspice-hover" onClick={() => setPreview(null)}>Close</Button>
          </div>
        </div>
      )}
    </div>
  );
}
